// ─────────────────────────────────────────────────────────────────────────────
// lib/contrast.js — leggibilita' dei colori (rapporto di contrasto WCAG)
//
// I colori che arrivano dal DB (brand della competizione, colori delle maglie) possono essere troppo chiari o troppo
// scuri per lo sfondo su cui li mettiamo: giallo su bianco, bianco su bianco, nero su sfondo scuro. Queste funzioni
// li sistemano senza cambiare la tinta.
//   contrast(a, b)           rapporto di contrasto tra due colori (1 = identici, 21 = bianco su nero)
//   textOn(bg)               '#FFFFFF' o '#111111', il piu' leggibile sopra bg
//   legibleOn(color, bg, min) lo stesso colore, con la luminosita' spostata fino a raggiungere il contrasto min su bg
// Soglie usate nell'app: 3 per testi e icone (il testo e' quasi sempre grande o in grassetto), 1.6 per segni
// decorativi (bordi, barre), cosi' una maglia bianca non sparisce sulla carta bianca.
// ─────────────────────────────────────────────────────────────────────────────

const HEX = /^#?([0-9a-f]{6})$/i

export function parseHex(hex) {
  const m = HEX.exec(String(hex ?? '').trim())
  if (!m) return null
  const n = parseInt(m[1], 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

const toHex = ([r, g, b]) => '#' + [r, g, b].map(v => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('')

function luminance(rgb) {
  const [r, g, b] = rgb.map(v => { const c = v / 255; return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4 })
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

export function contrast(a, b) {
  const A = parseHex(a), B = parseHex(b)
  if (!A || !B) return 21
  const [hi, lo] = [luminance(A), luminance(B)].sort((x, y) => y - x)
  return (hi + 0.05) / (lo + 0.05)
}

export function textOn(bg) {
  return contrast('#FFFFFF', bg) >= contrast('#111111', bg) ? '#FFFFFF' : '#111111'
}

function rgbToHsl([r, g, b]) {
  r /= 255; g /= 255; b /= 255
  const max = Math.max(r, g, b), min = Math.min(r, g, b)
  const l = (max + min) / 2
  if (max === min) return [0, 0, l]
  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  const h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4
  return [h / 6, s, l]
}

function hslToRgb([h, s, l]) {
  if (s === 0) return [l * 255, l * 255, l * 255]
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s, p = 2 * l - q
  const f = (t) => { t = (t + 1) % 1; return t < 1 / 6 ? p + (q - p) * 6 * t : t < 1 / 2 ? q : t < 2 / 3 ? p + (q - p) * (2 / 3 - t) * 6 : p }
  return [f(h + 1 / 3) * 255, f(h) * 255, f(h - 1 / 3) * 255]
}

// Stesso colore, ma leggibile su bg: se il contrasto e' gia' sufficiente non cambia nulla; altrimenti scurisce (sfondo
// chiaro) o schiarisce (sfondo scuro) a piccoli passi, mantenendo tinta e saturazione. Se non basta (bianco puro su
// bianco) arriva al nero o al bianco.
export function legibleOn(color, bg, min = 3) {
  const rgb = parseHex(color)
  if (!rgb || !parseHex(bg)) return color
  if (contrast(color, bg) >= min) return color
  const [h, s, l] = rgbToHsl(rgb)
  const goDark = luminance(parseHex(bg)) > 0.4          // sfondo chiaro: scurisco; sfondo scuro: schiarisco
  let out = color
  for (let i = 1; i <= 50; i++) {
    const nl = goDark ? l - i * 0.02 : l + i * 0.02
    out = toHex(hslToRgb([h, s, Math.max(0, Math.min(1, nl))]))
    if (contrast(out, bg) >= min) return out
    if (nl <= 0 || nl >= 1) break
  }
  return out
}
